import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
    Button,
    InputCheck,
    PasswordField,
} from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import { TenantMfaSettingsDocument } from '@graphql/generated/graphql';

import { AuthService } from '@services/AuthService/AuthService';
import { usePasswordReset } from '@services/AuthService/usePasswordReset';
import { getPath } from '@services/RouteService/paths';

import { useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { passwordRequirements } from '@shared/passwordRequirments';
import { passwordValid } from '@shared/validationFunctions';

interface ResetPasswordFormProps {
    code: string;
}

export const ResetPasswordForm = ({ code }: ResetPasswordFormProps) => {
    const { t } = useTranslation();
    const tenantUuid = AuthService.getTenantUuid();
    const navigate = useNavigate();

    const { data } = useQuery(TenantMfaSettingsDocument, {
        skip: !tenantUuid,
        variables: {
            uuid: tenantUuid || '',
        },
    });
    const showSteps = data?.tenant.enforceMfa;

    const { handleReset, resetProcessing } = usePasswordReset();

    const {
        formState: { dirtyFields, errors, isSubmitted, isValid },
        handleSubmit,
        register,
        watch,
    } = useForm<ResetPasswordFormValues>({
        mode: 'onChange',
        resolver: yupResolver(ResetPasswordSchema),
    });

    const passwordValue = watch('newPassword');
    const isPasswordCheckOpened =
        dirtyFields.newPassword || (!isValid && isSubmitted);

    const onSubmit = (formData: ResetPasswordFormValues) => {
        void handleReset(
            {
                input: { newPassword: formData.newPassword, token: code },
            },
            { onError: () => navigate(getPath({ path: '/auth/welcome' })) },
        );
    };

    return (
        <StandardForm>
            <PasswordField
                {...register('newPassword')}
                className="htmlToImageIgnore"
                error={!!errors.newPassword}
                label={t('FORGOT_PASSWORD_NEW_PASSWORD')}
                placeholder={t('FORGOT_PASSWORD_NEW_PASSWORD_PLACEHOLDER')}
                testId="YEFoB4I"
            />
            <InputCheck
                isDirty={isPasswordCheckOpened}
                isSubmitted={isSubmitted}
                passwordValue={passwordValue}
                requirements={passwordRequirements}
                testId="_loXOOW"
            />
            <PasswordField
                {...register('newPasswordConfirm')}
                className="htmlToImageIgnore"
                error={!!errors.newPasswordConfirm}
                label={t('FORGOT_PASSWORD_CONFIRM_NEW_PASSWORD')}
                message={t(errors.newPasswordConfirm?.message || '')}
                placeholder={t(
                    'FORGOT_PASSWORD_CONFIRM_NEW_PASSWORD_PLACEHOLDER',
                )}
                testId="rK78aJc"
            />
            <StandardForm.Actions>
                <Button
                    disabled={resetProcessing || (!isValid && isSubmitted)}
                    fullWidth
                    isLoading={resetProcessing}
                    onClick={handleSubmit(onSubmit)}
                    testId="button_send-new-password"
                    variant="primary"
                >
                    {t(
                        showSteps
                            ? 'SEND_NEW_PASSWORD'
                            : 'SEND_NEW_PASSWORD_WITHOUT_MFA',
                    )}
                </Button>
            </StandardForm.Actions>
        </StandardForm>
    );
};

const ResetPasswordSchema = yup.object({
    newPassword: yup
        .string()
        .required()
        .test('passwordValidation', '', passwordValid),
    newPasswordConfirm: yup
        .string()
        .required('FIELD_CANNOT_BE_EMPTY')
        .oneOf([yup.ref('newPassword')], 'PASSWORD_RESET_CONFIRM_DO_NOT_MATCH'),
});

type ResetPasswordFormValues = yup.InferType<typeof ResetPasswordSchema>;
